<!-- Main Content -->
<div class="page-content">
  <!-- Header Section -->
  <div class="page-title-container">
    <app-breadcrumb></app-breadcrumb>
    <h1 class="page-title">Offices</h1>
    @if (officeDetail && !isLoading && !error) {
      <div class="page-title-actions">
        <span
          class="badge"
          [ngClass]="officeDetail.active ? 'badge-active' : 'badge-inactive'"
        >
          {{ officeDetail.active ? "Active" : "Inactive" }}
        </span>
      </div>
    }
  </div>

  <!-- Loading State -->
  @if (isLoading) {
    <mat-card class="details-card">
      <div class="loading-state">
        <mat-spinner diameter="32"></mat-spinner>
        <p>Loading office details...</p>
      </div>
    </mat-card>
  }

  <!-- Error State -->
  @if (error && !isLoading) {
    <mat-card class="details-card">
      <div class="error-state">
        <i class="material-symbols-outlined error-icon">error</i>
        <h3>Error Loading Office</h3>
        <p>{{ error }}</p>
        <button mat-raised-button color="primary" (click)="retry()">
          <mat-icon>refresh</mat-icon>
          Retry
        </button>
      </div>
    </mat-card>
  }

  <!-- Office Profile Card and Policies Row -->
  @if (!isLoading && !error && officeCode) {
    <mat-card class="details-card office-profile-card">
      <div class="office-details">
        <div class="resource-profile">
          <div class="avatar-container">
            <div class="office-avatar">
              <mat-icon>location_city</mat-icon>
            </div>
          </div>
          <div class="office-info">
            <div class="name-and-id">
              <h1 class="office-name">{{ officeDescription }}</h1>
              <app-copyable-text
                [value]="officeCode"
                [displayText]="'ID: ' + officeCode"
                copyTitle="Click to copy office code"
                [uppercase]="true"
              />
            </div>

            @if (officeDetail) {
              @if (officeDetail.addressLine1) {
                <div class="office-details-row">
                  <div class="detail-group">
                    <mat-icon class="detail-icon">location_on</mat-icon>
                    <span class="detail-text">
                      {{ officeDetail.addressLine1 }}
                      @if (officeDetail.addressLine2) {
                        , {{ officeDetail.addressLine2 }}
                      }
                      @if (officeDetail.city) {
                        , {{ officeDetail.city }}
                      }
                      @if (officeDetail.state) {
                        , {{ officeDetail.state }}
                      }
                      @if (officeDetail.zip) {
                        {{ officeDetail.zip }}
                      }
                    </span>
                  </div>
                </div>
              }

              <div class="office-details-row">
                @if (officeDetail.telephone) {
                  <div class="detail-group">
                    <mat-icon class="detail-icon">phone</mat-icon>
                    <span class="detail-text">{{
                      officeDetail.telephone
                    }}</span>
                  </div>
                }
                @if (officeDetail.fax) {
                  <div class="detail-group">
                    <mat-icon class="detail-icon">fax</mat-icon>
                    <span class="detail-text">{{ officeDetail.fax }}</span>
                  </div>
                }
                <div class="detail-group">
                  <mat-icon class="detail-icon">people</mat-icon>
                  <span class="detail-text"
                    >{{ officeDetail.resourceCount }} Resources</span
                  >
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </mat-card>

    @if (canViewTimeOffPolicies) {
      <mat-card class="details-card policies-card">
        <div class="policies-section">
          <div class="card-heading">
            <div class="section-header">
              <h3>Time-Off Policy</h3>
              @if (canEditTimeOffPolicies) {
                <button
                  mat-stroked-button
                  class="section-action-btn"
                  (click)="openTimeOffPoliciesDialog()"
                  [disabled]="isSaving"
                >
                  <mat-icon>edit</mat-icon>
                  Edit Policies
                </button>
              }
            </div>
            <p class="card-description">
              The default time-off allotments for resources in this office. A
              practice area can override the Vacation policy for resources whose
              primary practice area it is, and individual resources can have
              their own overrides.
            </p>
          </div>

          <div class="policy-list">
            @for (policy of dataSource.data; track policy.typeCode) {
              <div class="value-card policy-item">
                <span class="value-card__label">{{ policy.typeName }}</span>
                <span class="policy-item__values">
                  <strong>{{ policy.defaultDays }}</strong> days default
                  <span class="policy-item__sep">·</span>
                  <strong>
                    @if (policy.maxCarryoverDays !== null) {
                      {{ policy.maxCarryoverDays }}
                    } @else {
                      <!-- A null cap means carry over everything, not zero — so
                           this must not read as an empty value. role="img" is
                           what makes the aria-label authoritative; on a bare
                           span ARIA prohibits naming and readers fall back to
                           announcing the glyph itself ("infinity"). tabindex
                           lets keyboard users open the tooltip, which is the
                           only place the glyph's meaning is spelled out. -->
                      <span
                        class="policy-item__unlimited"
                        role="img"
                        tabindex="0"
                        matTooltip="No cap configured — unused days are not forfeited"
                        aria-label="Unlimited"
                        >∞</span
                      >
                    }
                  </strong>
                  max carryover
                </span>
              </div>
            }
          </div>

          <!-- Per-practice-area Vacation overrides for this office (collapsible).
               Every active row is in force — unlike resource overrides there is
               no effective-year dimension to resolve.
               The id is the anchor the resource policy dialog's link points at; the
               template ref is what the component scrolls to once the rows load. -->
          <div
            class="overrides-subsection"
            id="practice-area-overrides"
            #practiceAreaOverridesSection
          >
            <button
              type="button"
              class="overrides-toggle"
              (click)="
                practiceAreaOverridesExpanded = !practiceAreaOverridesExpanded
              "
              [attr.aria-expanded]="practiceAreaOverridesExpanded"
            >
              <mat-icon
                class="expansion-indicator"
                [style.transform]="
                  practiceAreaOverridesExpanded
                    ? 'rotate(0deg)'
                    : 'rotate(-90deg)'
                "
                >expand_more</mat-icon
              >
              <span class="overrides-toggle__title"
                >Practice Area Overrides</span
              >
              <span class="overrides-toggle__count"
                >({{ practiceAreaOverridesDataSource.data.length }})</span
              >
            </button>

            @if (practiceAreaOverridesExpanded) {
              @if (practiceAreaOverridesDataSource.data.length === 0) {
                <div class="overrides-empty">
                  <span>No practice area overrides for this office.</span>
                </div>
              } @else {
                <div
                  class="table-container overrides-table practice-area-overrides-table"
                >
                  <table
                    mat-table
                    [dataSource]="practiceAreaOverridesDataSource"
                    class="policies-mat-table"
                  >
                    <!-- Practice Area Column -->
                    <ng-container matColumnDef="practiceArea">
                      <th mat-header-cell *matHeaderCellDef>Practice Area</th>
                      <td mat-cell *matCellDef="let row">
                        <div class="practice-area-name">
                          {{ row.practiceAreaName }}
                        </div>
                        <div class="practice-area-code">
                          {{ row.practiceAreaCode }}
                        </div>
                      </td>
                    </ng-container>

                    <!-- Type Column -->
                    <ng-container matColumnDef="typeName">
                      <th mat-header-cell *matHeaderCellDef>Type</th>
                      <td mat-cell *matCellDef="let row">
                        {{ row.typeName }}
                      </td>
                    </ng-container>

                    <!-- Default Days Column -->
                    <ng-container matColumnDef="defaultDays">
                      <th mat-header-cell *matHeaderCellDef>Default Days</th>
                      <td mat-cell *matCellDef="let row">
                        {{ row.defaultDays }}
                      </td>
                    </ng-container>

                    <!-- Max Carryover Column -->
                    <ng-container matColumnDef="maxCarryoverDays">
                      <th mat-header-cell *matHeaderCellDef>Max Carryover</th>
                      <td mat-cell *matCellDef="let row">
                        @if (row.maxCarryoverDays !== null) {
                          {{ row.maxCarryoverDays }}
                        } @else {
                          <!-- Same meaning as the ∞ on the default card above: no
                               cap, carry over everything. No tabindex here — one
                               row per override would make the focus stops
                               unbounded. -->
                          <span
                            class="policy-item__unlimited"
                            role="img"
                            matTooltip="No cap configured — unused days are not forfeited"
                            aria-label="Unlimited"
                            >∞</span
                          >
                        }
                      </td>
                    </ng-container>

                    <tr
                      mat-header-row
                      *matHeaderRowDef="practiceAreaOverrideDisplayedColumns"
                    ></tr>
                    <tr
                      mat-row
                      *matRowDef="
                        let row;
                        columns: practiceAreaOverrideDisplayedColumns
                      "
                    ></tr>
                  </table>
                </div>
              }
            }
          </div>

          <!-- Per-resource overrides for this office (collapsible) -->
          <div class="overrides-subsection">
            <button
              type="button"
              class="overrides-toggle"
              (click)="timeOffOverridesExpanded = !timeOffOverridesExpanded"
              [attr.aria-expanded]="timeOffOverridesExpanded"
            >
              <mat-icon
                class="expansion-indicator"
                [style.transform]="
                  timeOffOverridesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                "
                >expand_more</mat-icon
              >
              <span class="overrides-toggle__title">Resource Overrides</span>
              <span class="overrides-toggle__count"
                >({{ overridesDataSource.data.length }})</span
              >
            </button>

            @if (timeOffOverridesExpanded) {
              @if (overridesDataSource.data.length === 0) {
                <div class="overrides-empty">
                  <span>No resource overrides for this office.</span>
                </div>
              } @else {
                <div class="table-container overrides-table">
                  <table
                    mat-table
                    [dataSource]="overridesDataSource"
                    class="policies-mat-table"
                  >
                    <!-- Resource Name Column -->
                    <ng-container matColumnDef="resourceName">
                      <th mat-header-cell *matHeaderCellDef>Resource</th>
                      <td mat-cell *matCellDef="let row">
                        @if (canAccessResource(row)) {
                          <a
                            class="resource-cell resource-cell-link"
                            (click)="navigateToResource(row.employeeId, $event)"
                          >
                            <div class="resource-name">
                              {{ row.employeeName }}
                            </div>
                          </a>
                        } @else {
                          <div class="resource-name resource-name-plain">
                            {{ row.employeeName }}
                          </div>
                        }
                      </td>
                    </ng-container>

                    <!-- Type Column -->
                    <ng-container matColumnDef="typeName">
                      <th mat-header-cell *matHeaderCellDef>Type</th>
                      <td mat-cell *matCellDef="let row">
                        {{ row.typeName }}
                      </td>
                    </ng-container>

                    <!-- Effective Year Column -->
                    <ng-container matColumnDef="effectiveYear">
                      <th mat-header-cell *matHeaderCellDef>Effective Year</th>
                      <td mat-cell *matCellDef="let row">
                        {{ row.effectiveYear ?? "Baseline" }}
                      </td>
                    </ng-container>

                    <!-- Default Days Column -->
                    <ng-container matColumnDef="defaultDays">
                      <th mat-header-cell *matHeaderCellDef>Default Days</th>
                      <td mat-cell *matCellDef="let row">
                        {{ row.defaultDays }}
                      </td>
                    </ng-container>

                    <!-- Carryover Override Column -->
                    <ng-container matColumnDef="carryoverDaysOverride">
                      <th mat-header-cell *matHeaderCellDef>
                        Carryover Amount
                      </th>
                      <td mat-cell *matCellDef="let row">
                        @if (row.carryoverDaysAmount !== null) {
                          {{ row.carryoverDaysAmount }}
                        } @else {
                          <!-- A null override means "inherits the office
                               policy" — a different meaning from the ∞ above,
                               which is why the dash needs spelling out too. No
                               tabindex here: this table has a row per override,
                               so a focus stop would be unbounded. -->
                          <span
                            role="img"
                            aria-label="No override"
                            matTooltip="No override — inherits the office policy"
                            >—</span
                          >
                        }
                      </td>
                    </ng-container>

                    <tr
                      mat-header-row
                      *matHeaderRowDef="overrideDisplayedColumns"
                    ></tr>
                    <tr
                      mat-row
                      *matRowDef="let row; columns: overrideDisplayedColumns"
                    ></tr>
                  </table>
                </div>
              }
            }
          </div>
        </div>
      </mat-card>
    }

    <!-- Notifications (office-level, extracted into its own component) -->
    @if (
      canViewOfficeNotifications &&
      officeCode &&
      scheduledOfficeNotificationsEnabled
    ) {
      <app-office-notifications
        [officeCode]="officeCode"
        [officeDescription]="officeDescription"
      ></app-office-notifications>
    }

    <!-- Practice Area Settings (per-office, per-practice-area capacity settings).
         Deliberately NOT behind expectedPeriodHoursEnabled: the capacity period this card
         sets is read by a rule that the flag does not gate, so the card has to be reachable
         in production. The flag is handed to the card instead, where it gates only the
         expected-hours column. -->
    @if (canViewOfficePracticeAreaSettings && officeCode) {
      <app-office-practice-area-settings
        [officeCode]="officeCode"
        [officeDescription]="officeDescription"
        [expectedHoursEnabled]="expectedPeriodHoursEnabled"
      ></app-office-practice-area-settings>
    }

    <!-- Office Resources (shared dual-list panel) -->
    @if (canViewSecondaryOffices && secondaryOfficeDataSource) {
      <app-resource-membership-panel
        [dataSource]="secondaryOfficeDataSource"
        title="Office Resources"
      ></app-resource-membership-panel>
    }
  }
</div>
